import { ReactNode, useEffect, useMemo, useState } from 'react';
import { styled } from '@mui/material/styles';
import {
  Box,
  Divider,
  Drawer,
  Grid,
  List,
  ListItem,
  Stack,
  Typography,
  Button,
  Chip,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import SimpleBar from 'components/third-party/SimpleBar';
import { Add, Slash, ShoppingCart } from 'iconsax-react';
import { ThemeMode } from 'types/config';
import serverCall from 'api/serverCall';
import api from 'api/api';
import CancelIcon from '@mui/icons-material/Cancel';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import {
  getOrderStatusLabel,
  getOrderTypeLabel,
  isStopLossOrder,
  isTerminalOrderStatus,
  OrderRequest,
  OrderRequestStatus
} from 'types/custom/order';
import { getOrderStatusColor } from 'utils/custom';
import Amount from 'components/common/Amount';
import { dispatch } from 'store';
import { openSnackbar } from 'store/reducers/snackbar';
import { useWebsocketContext } from 'contexts/WebSocketContext';
import Avatar from 'components/@extended/Avatar';
import Alert from 'components/alert/CommonAlert';
import { formatDateCommon, formatNumberToCurrency } from 'utils/formatters';
import timeAgo from 'utils/timeAgo';
import { CurrencyType } from 'enum/currency';
import { useFirebaseContext } from 'contexts/FirebaseContext';
import { SimulationSource } from 'types/custom/simulation';
import ChangeOrderQuantityModal, { EditOrderState, emptyEditOrderState } from './ChangeOrderQuantity';
import EditIcon from '@mui/icons-material/Edit';
import StockDetailModal, { StockDetailState, emptyStockDetailState } from './StockDetailModal';

/** Just the fields of a placed order this drawer reads back from the broker. */
type PlacedOrder = { orderId: number; price: number; triggerPrice: number };

const StyledGridOverlay = styled(Stack)(({ theme }) => ({
  height: '400px',
  '& .ant-empty-img-1': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[200] : theme.palette.secondary[400]
  },
  '& .ant-empty-img-2': {
    fill: theme.palette.secondary.light
  },
  '& .ant-empty-img-3': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary[400] : theme.palette.secondary[200]
  },
  '& .ant-empty-img-4': {
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.dark : theme.palette.common.white
  },
  '& .ant-empty-img-5': {
    fillOpacity: theme.palette.mode === ThemeMode.DARK ? '0.09' : '0.95',
    fill: theme.palette.mode === ThemeMode.DARK ? theme.palette.secondary.darker : theme.palette.secondary.light
  }
}));

/**
 * What the broker did with an approved request. Rendered only once the request
 * has been approved — before that there is no order to report on.
 *
 * The requested price is already shown above this block. A MARKET order has
 * none, so the executed price is the only indication of what it cost; a LIMIT
 * order has one, but it is what was asked for rather than what was paid — so
 * the executed price is worth showing either way.
 */
const OrderExecutionDetails: React.FC<{ order: OrderRequest }> = ({ order }) => {
  if (order.orderRequestStatus !== OrderRequestStatus.APPROVED || !order.orderStatus) return null;

  const executedQuantity = order.executedQuantity ?? 0;
  const executedPrice = order.executedPrice ?? 0;
  const partiallyFilled = executedQuantity > 0 && order.quantity > 0 && executedQuantity < order.quantity;

  return (
    <Stack spacing={0.25} sx={{ mt: 0.25 }}>
      <Stack direction="row" spacing={0.75} alignItems="center">
        <Typography variant="caption" color="text.secondary">
          Status:
        </Typography>
        <Chip label={getOrderStatusLabel(order.orderStatus)} color={getOrderStatusColor(order.orderStatus)} size="small" variant="light" />
        {partiallyFilled && <Chip label="Partial" color="warning" size="small" variant="light" />}
      </Stack>

      {executedQuantity > 0 ? (
        <>
          <Typography variant="caption" color="text.secondary">
            Executed QTY: {executedQuantity}
            {order.quantity > 0 ? ` of ${order.quantity}` : ''}
          </Typography>
          {executedPrice > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              Executed Price:&nbsp;
              <Amount amount={executedPrice} variant="subtitle2" symbol="₹" />
            </Typography>
          )}
          {executedPrice > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
              Executed Value:&nbsp;
              <Amount amount={executedPrice * executedQuantity} variant="subtitle2" symbol="₹" />
            </Typography>
          )}
        </>
      ) : (
        !isTerminalOrderStatus(order.orderStatus) && (
          <Typography variant="caption" color="text.secondary">
            Awaiting execution at broker
          </Typography>
        )
      )}

      {order.executedOn && (
        <Typography variant="caption" color="text.secondary">
          Executed At: {formatDateCommon(order.executedOn, 'dd-MMM-yyyy, hh:mm:ss a')}
        </Typography>
      )}

      {order.errorMessage && (
        <Typography
          variant="caption"
          sx={{
            pl: 0.75,
            pr: 0.75,
            py: 0.25,
            borderRadius: 1,
            display: 'inline-block',
            bgcolor: 'error.lighter',
            color: 'error.dark',
            fontWeight: 600
          }}
        >
          ⚠ {order.errorMessage}
        </Typography>
      )}
    </Stack>
  );
};

/** How often to re-check an approved order that has not finished filling. */
const EXECUTION_POLL_INTERVAL_MS = 4000;

type Props = {
  open: boolean;
  setOpen: (val: boolean) => void;
  setOrderRequestCount: (val: number) => void;
};

const OrderRequestDrawer: React.FC<Props> = ({ open, setOpen, setOrderRequestCount }) => {
  const { orderRequestStatus, orderStatusChanged, liveTicks, orderRequestQuantities } = useWebsocketContext();
  const [orders, setOrders] = useState<OrderRequest[]>([]);
  const [transactionFilter, setTransactionFilter] = useState<'ALL' | 'BUY' | 'SELL'>('ALL');
  const [accountFilter, setAccountFilter] = useState<string>('ALL');
  const [, setPriceRefreshKey] = useState(0);
  const [mtfToggle, setMtfToggle] = useState<Record<number, boolean>>({});
  const [isOpenCancelOrderConfirmation, setIsOpenCancelOrderConfirmation] = useState<boolean>(false);
  const { user } = useFirebaseContext();
  const permissions = user?.permissions;
  const hasApproveOrderPermission = permissions?.includes('canApproveOrderRequest');
  const [changeQtyState, setChangeQtyState] = useState<EditOrderState>(emptyEditOrderState);
  const [stockDetailState, setStockDetailState] = useState<StockDetailState>(emptyStockDetailState);

  const openStockDetail = (stockId?: string) => {
    if (stockId) setStockDetailState({ open: true, stockId });
  };

  /**
   * The quantity approval would place. The server re-sizes an open request as the
   * price moves and pushes the result on its own channel, the same way the LTP
   * arrives; until one turns up, the request's own quantity stands.
   */
  const liveQuantity = (order: OrderRequest): number | null => {
    const pushed = orderRequestQuantities.current[order.requestId];
    return pushed != null && pushed > 0 && pushed !== order.quantity ? pushed : null;
  };

  const handleToggle = () => {
    setOpen(!open);
  };

  const fetchAllOrderRequests = () => {
    serverCall.get(api.getAllPendingOrders(), (response, error) => {
      if (error) {
        console.error(error);
      } else {
        const fetched: OrderRequest[] = response.orders;
        setOrders(fetched);
        setOrderRequestCount(response.totalCount ?? 0);
        setMtfToggle(
          fetched.reduce((acc: Record<number, boolean>, o: OrderRequest) => {
            acc[o.requestId] = o.useMTF ?? false;
            return acc;
          }, {})
        );
      }
    });
  };

  useEffect(() => {
    fetchAllOrderRequests();
  }, [orderRequestStatus, orderStatusChanged]);

  useEffect(() => {
    if (open) fetchAllOrderRequests();
  }, [open]);

  /**
   * An SL order is re-priced off the LTP at the moment it is placed, so the
   * trigger and limit the broker ends up holding are not the ones on the
   * request. Those land on the placed order, which this payload does not carry.
   *
   * Keyed on orderId, not requestId: the order rows come back with requestId 0,
   * while orderId matches the request on both sides. Only stop-loss rows need
   * this, which keeps it to a call or two.
   */
  const [brokerPrices, setBrokerPrices] = useState<Record<number, { price: number; triggerPrice: number }>>({});

  const stopLossUserKey = useMemo(
    () =>
      Array.from(new Set(orders.filter((o) => isStopLossOrder(o.orderType) && o.userId).map((o) => o.userId)))
        .sort()
        .join(','),
    [orders]
  );

  useEffect(() => {
    if (!open || !stopLossUserKey) return;
    stopLossUserKey.split(',').forEach((userId) => {
      serverCall.get(api.getAllOrdersByUser(userId), (response, error) => {
        if (error || !response) return;
        const placedOrders: PlacedOrder[] = Array.isArray(response) ? response : response.orders ?? [];

        // The same orderId can appear twice - the stored row first, then an
        // in-memory copy that may still hold a pre-repricing limit. Keep the
        // first of each within this response, or a stale leg overwrites a good
        // one; then let the response refresh whatever an earlier fetch stored.
        const fromThisFetch: Record<number, { price: number; triggerPrice: number }> = {};
        placedOrders.forEach((placed) => {
          if (placed?.orderId && fromThisFetch[placed.orderId] === undefined) {
            fromThisFetch[placed.orderId] = { price: placed.price, triggerPrice: placed.triggerPrice };
          }
        });
        setBrokerPrices((previous) => ({ ...previous, ...fromThisFetch }));
      });
    });
  }, [open, stopLossUserKey]);

  // Live LTP is stored on a ref (no re-renders), so periodically refresh while the drawer is open.
  useEffect(() => {
    if (!open) return;
    const interval = setInterval(() => setPriceRefreshKey((key) => key + 1), 1000);
    return () => clearInterval(interval);
  }, [open]);

  // An approved order can keep filling without changing status — a LIMIT order
  // especially — and only a status change pushes a notification, so poll while
  // any approved order is still working at the broker.
  const hasWorkingOrder = orders.some((o) => o.orderRequestStatus === OrderRequestStatus.APPROVED && !isTerminalOrderStatus(o.orderStatus));

  useEffect(() => {
    if (!open || !hasWorkingOrder) return;
    const interval = setInterval(fetchAllOrderRequests, EXECUTION_POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [open, hasWorkingOrder]);

  const handleClearAll = () => {
    serverCall.put(api.clearAllOrders(SimulationSource.LIVE), {}, (response, error) => {
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Error declining all orders`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        fetchAllOrderRequests();
        setOrderRequestCount(0);
        setIsOpenCancelOrderConfirmation(false);
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `All orders cancelled successfully`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      }
    });
  };

  const handleDeclineOrder = (requestId: string, orderId: string, userId: string) => {
    serverCall.put(api.declineOrderRequest(requestId, orderId, userId), {}, (response, error) => {
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Error declining order request ${requestId}`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        fetchAllOrderRequests();
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Order request ${requestId} declined successfully`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      }
    });
  };

  const handleApproveOrder = (orderId: string, useMTF: boolean, quantity?: number) => {
    const body = quantity != null ? { useMTF, quantity } : { useMTF };
    serverCall.put(api.approveOrderRequest(orderId, SimulationSource.LIVE), body, (response, error) => {
      if (error) {
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Error approving order ${orderId}`,
            variant: 'alert',
            alert: {
              color: 'error'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      } else {
        fetchAllOrderRequests();
        dispatch(
          openSnackbar({
            open: true,
            close: true,
            message: `Order ${orderId} approved successfully`,
            variant: 'alert',
            alert: {
              color: 'success'
            },
            anchorOrigin: {
              vertical: 'top',
              horizontal: 'center'
            }
          })
        );
      }
    });
  };

  const handleCloseModal = () => {
    setIsOpenCancelOrderConfirmation(false);
  };

  const accountOptions = useMemo(() => {
    const map = new Map<string, string>();
    orders.forEach((o) => {
      if (o.userId && !map.has(o.userId)) {
        map.set(o.userId, `${o.firstName ?? ''} ${o.lastName ?? ''}`.trim() || 'Unknown User');
      }
    });
    return Array.from(map.entries()).map(([userId, name]) => ({ userId, name }));
  }, [orders]);

  const filteredOrders = orders.filter(
    (o) =>
      (transactionFilter === 'ALL' || o.transactionType === transactionFilter) && (accountFilter === 'ALL' || o.userId === accountFilter)
  );

  const alertContent: ReactNode = (
    <Stack alignItems="center" spacing={3.5}>
      <Avatar color="warning" sx={{ width: 72, height: 72, fontSize: '1.75rem' }}>
        <Slash variant="Bold" />
      </Avatar>
      <Stack spacing={2}>
        <Typography variant="h4" align="center">
          Confirm Order Clearing
        </Typography>
        <Typography align="center">Are you sure you want to clear all orders?</Typography>
      </Stack>

      <Stack direction="row" spacing={2} sx={{ width: 1 }}>
        <Button fullWidth onClick={handleCloseModal} color="secondary" variant="outlined">
          No, Keep Orders
        </Button>
        <Button fullWidth color="primary" variant="contained" onClick={handleClearAll} autoFocus>
          Yes, Clear Orders
        </Button>
      </Stack>
    </Stack>
  );

  return (
    <>
      {isOpenCancelOrderConfirmation && (
        <Alert open={isOpenCancelOrderConfirmation} handleClose={handleCloseModal} content={alertContent} />
      )}

      {changeQtyState.open && (
        <ChangeOrderQuantityModal state={changeQtyState} setState={setChangeQtyState} refetchSimulation={fetchAllOrderRequests} />
      )}

      {stockDetailState.open && <StockDetailModal state={stockDetailState} setState={setStockDetailState} />}

      <Drawer sx={{ zIndex: 2001 }} anchor="right" onClose={handleToggle} open={open} PaperProps={{ sx: { width: { xs: 350, sm: 474 } } }}>
        {open && (
          <MainCard content={false} sx={{ border: 'none', borderRadius: 0, height: '100vh' }}>
            <SimpleBar
              sx={{
                '& .simplebar-content': {
                  display: 'flex',
                  flexDirection: 'column'
                }
              }}
            >
              <Box sx={{ p: 2.5 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1.5}>
                  <Typography variant="h5">Order Requests</Typography>
                  <IconButton color="secondary" sx={{ p: 0 }} onClick={handleToggle}>
                    <Add size={28} style={{ transform: 'rotate(45deg)' }} />
                  </IconButton>
                </Stack>
                <Divider sx={{ mt: 1.5, mb: 2 }} />
                {orders.length > 0 && (
                  <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1} sx={{ mb: 1.5 }}>
                    <Tabs value={transactionFilter} onChange={(e, val) => setTransactionFilter(val)} sx={{ minHeight: 36 }}>
                      <Tab label="All" value="ALL" sx={{ minHeight: 36, py: 0.5 }} />
                      <Tab label="Buy" value="BUY" sx={{ minHeight: 36, py: 0.5 }} />
                      <Tab label="Sell" value="SELL" sx={{ minHeight: 36, py: 0.5 }} />
                    </Tabs>
                    <FormControl size="small" sx={{ minWidth: 140 }}>
                      <InputLabel id="account-filter-label">Account</InputLabel>
                      <Select
                        labelId="account-filter-label"
                        label="Account"
                        value={accountFilter}
                        onChange={(e) => setAccountFilter(e.target.value)}
                        MenuProps={{ sx: { zIndex: 2101 } }}
                      >
                        <MenuItem value="ALL">All Accounts</MenuItem>
                        {accountOptions.map((a) => (
                          <MenuItem key={a.userId} value={a.userId}>
                            {a.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Stack>
                )}
                {orders.length > 0 && (
                  <Box sx={{ mb: 2 }} justifyContent="end" display="flex">
                    <Button
                      variant="outlined"
                      color="error"
                      size="small"
                      onClick={() => {
                        setIsOpenCancelOrderConfirmation(true);
                      }}
                      startIcon={<CancelIcon />}
                    >
                      Clear All Orders
                    </Button>
                  </Box>
                )}
                <Grid>
                  {filteredOrders.length === 0 ? (
                    <StyledGridOverlay alignItems="center" justifyContent="center" spacing={1}>
                      <svg width="120" height="100" viewBox="0 0 184 152" aria-hidden focusable="false">
                        <g fill="none" fillRule="evenodd">
                          <g transform="translate(24 31.67)">
                            <ellipse className="ant-empty-img-5" cx="67.797" cy="106.89" rx="67.797" ry="12.668" />
                            <path
                              className="ant-empty-img-1"
                              d="M122.034 69.674L98.109 40.229c-1.148-1.386-2.826-2.225-4.593-2.225h-51.44c-1.766 0-3.444.839-4.592 2.225L13.56 69.674v15.383h108.475V69.674z"
                            />
                            <path
                              className="ant-empty-img-2"
                              d="M33.83 0h67.933a4 4 0 0 1 4 4v93.344a4 4 0 0 1-4 4H33.83a4 4 0 0 1-4-4V4a4 4 0 0 1 4-4z"
                            />
                            <path
                              className="ant-empty-img-3"
                              d="M42.678 9.953h50.237a2 2 0 0 1 2 2V36.91a2 2 0 0 1-2 2H42.678a2 2 0 0 1-2-2V11.953a2 2 0 0 1 2-2zM42.94 49.767h49.713a2.262 2.262 0 1 1 0 4.524H42.94a2.262 2.262 0 0 1 0-4.524zM42.94 61.53h49.713a2.262 2.262 0 1 1 0 4.525H42.94a2.262 2.262 0 0 1 0-4.525zM121.813 105.032c-.775 3.071-3.497 5.36-6.735 5.36H20.515c-3.238 0-5.96-2.29-6.734-5.36a7.309 7.309 0 0 1-.222-1.79V69.675h26.318c2.907 0 5.25 2.448 5.25 5.42v.04c0 2.971 2.37 5.37 5.277 5.37h34.785c2.907 0 5.277-2.421 5.277-5.393V75.1c0-2.972 2.343-5.426 5.25-5.426h26.318v33.569c0 .617-.077 1.216-.221 1.789z"
                            />
                          </g>
                          <path
                            className="ant-empty-img-3"
                            d="M149.121 33.292l-6.83 2.65a1 1 0 0 1-1.317-1.23l1.937-6.207c-2.589-2.944-4.109-6.534-4.109-10.408C138.802 8.102 148.92 0 161.402 0 173.881 0 184 8.102 184 18.097c0 9.995-10.118 18.097-22.599 18.097-4.528 0-8.744-1.066-12.28-2.902z"
                          />
                          <g className="ant-empty-img-4" transform="translate(149.65 15.383)">
                            <ellipse cx="20.654" cy="3.167" rx="2.849" ry="2.815" />
                            <path d="M5.698 5.63H0L2.898.704zM9.259.704h4.985V5.63H9.259z" />
                          </g>
                        </g>
                      </svg>
                      <Typography align="center">Oops! No orders requests found!.</Typography>
                    </StyledGridOverlay>
                  ) : (
                    <List disablePadding sx={{ '& .MuiListItem-root': { py: 1.5, borderBottom: '1px solid', borderColor: 'divider' } }}>
                      {filteredOrders
                        .sort((a, b) => new Date(b.createdOn).getTime() - new Date(a.createdOn).getTime())
                        .map((order) => (
                          <ListItem
                            key={order.requestId}
                            sx={{
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'stretch',
                              '&:hover': {
                                backgroundColor: 'action.hover'
                              }
                            }}
                          >
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                              <Typography variant="subtitle2">
                                {`${order?.firstName ?? ''} ${order?.lastName ?? ''}`.trim() || 'Unknown User'} - {order?.stockId ?? ''}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {order.createdOn
                                  ? timeAgo(order.createdOn) + ', ' + formatDateCommon(order.createdOn, 'dd-MMM-yyyy, hh:mm:ss a')
                                  : 'N/A'}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', width: '100%', mb: 1 }}>
                              <Stack direction="row" spacing={1} alignItems="center">
                                <Typography
                                  variant="subtitle1"
                                  sx={{
                                    fontWeight: 'bold',
                                    ...(order.stockId && {
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                      textDecorationStyle: 'dotted',
                                      '&:hover': { color: 'primary.main' }
                                    })
                                  }}
                                  onClick={() => openStockDetail(order.stockId)}
                                  title={order.stockId ? 'View stock details' : undefined}
                                >
                                  {order.basketOrder ? order.stockName : order.stockId ?? '--'}
                                </Typography>
                                {getOrderTypeLabel(order.orderType) && (
                                  <Chip
                                    label={getOrderTypeLabel(order.orderType)}
                                    size="small"
                                    variant="light"
                                    color={order.orderType === 'SL' || order.orderType === 'SLM' ? 'warning' : 'info'}
                                  />
                                )}
                              </Stack>
                              {order.basketOrder && (
                                <div style={{ marginRight: '3px' }}>
                                  <ShoppingCart size={20} />
                                </div>
                              )}
                            </Box>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
                              <Stack spacing={0.5} sx={{ flex: 1 }}>
                                {!order.basketOrder && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                    QTY:&nbsp;
                                    {liveQuantity(order) != null ? (
                                      <>
                                        <Box component="span" sx={{ textDecoration: 'line-through', opacity: 0.6 }}>
                                          {order?.quantity ?? 0}
                                        </Box>
                                        &nbsp;
                                        <Box component="span" sx={{ fontWeight: 700, color: 'warning.dark' }}>
                                          {liveQuantity(order)}
                                        </Box>
                                      </>
                                    ) : (
                                      <>{order?.quantity ?? 0}</>
                                    )}
                                    &nbsp;| {isStopLossOrder(order.orderType) ? 'Limit' : 'Price'}:&nbsp;
                                    <Amount amount={brokerPrices[order.orderId]?.price ?? order.price} variant="subtitle2" symbol="₹" />
                                  </Typography>
                                )}
                                {/*
                                  Read from the order the broker holds rather than the request: the
                                  request keeps the pre-placement figure, and showing that was what
                                  made an approved order look like it went over at another price.
                                */}
                                {!order.basketOrder && isStopLossOrder(order.orderType) && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                    Trigger:&nbsp;
                                    <Amount
                                      amount={brokerPrices[order.orderId]?.triggerPrice ?? order.triggerPrice}
                                      variant="subtitle2"
                                      symbol="₹"
                                    />
                                    {order.stoploss > 0 && (
                                      <>
                                        &nbsp;| Stoploss:&nbsp;
                                        <Amount amount={order.stoploss} variant="subtitle2" symbol="₹" />
                                      </>
                                    )}
                                  </Typography>
                                )}
                                {!order.basketOrder && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                    LTP:&nbsp;
                                    {order.stockId && liveTicks.current[order.stockId]?.price != null ? (
                                      <Amount amount={liveTicks.current[order.stockId].price} variant="subtitle2" symbol="₹" />
                                    ) : (
                                      <Typography variant="subtitle2" component="span">
                                        --
                                      </Typography>
                                    )}
                                  </Typography>
                                )}
                                {!order.basketOrder && order?.price > 0 && order?.quantity > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                    Total:&nbsp;
                                    <Amount amount={order.price * order.quantity} variant="subtitle2" symbol="₹" />
                                  </Typography>
                                )}
                                {order.basketOrder && order.orderRequestList?.length > 0 && (
                                  <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                    Total:&nbsp;
                                    <Amount
                                      amount={order.orderRequestList.reduce((sum, r) => sum + (r.price ?? 0) * (r.quantity ?? 0), 0)}
                                      variant="subtitle2"
                                      symbol="₹"
                                    />
                                  </Typography>
                                )}
                                <OrderExecutionDetails order={order} />
                                {order.insufficientFunds && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      pl: 0.75,
                                      pr: 0.75,
                                      py: 0.25,
                                      borderRadius: 1,
                                      display: 'inline-block',
                                      bgcolor: 'error.lighter',
                                      color: 'error.dark',
                                      fontWeight: 600
                                    }}
                                  >
                                    ⚠ Insufficient balance
                                  </Typography>
                                )}
                                {order.loggedIn === false && (
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      pl: 0.75,
                                      pr: 0.75,
                                      py: 0.25,
                                      borderRadius: 1,
                                      display: 'inline-block',
                                      bgcolor: 'warning.lighter',
                                      color: 'warning.dark',
                                      fontWeight: 600
                                    }}
                                  >
                                    ⚠ User is not logged in — broker session inactive
                                  </Typography>
                                )}
                                {order.reasons && order.reasons.length > 0 && (
                                  <Typography variant="caption" color="text.secondary">
                                    Reason: {order.reasons.join(', ')}
                                  </Typography>
                                )}
                                {!order.insufficientFunds &&
                                  order.orderRequestStatus === OrderRequestStatus.OPEN &&
                                  (() => {
                                    const isOptionsOrder =
                                      order.option === true ||
                                      (order.basketOrder && order.orderRequestList?.every((r) => r.option === true));
                                    if (isOptionsOrder) return null;
                                    // For SELL orders, only show the toggle if it's an MTF order
                                    if (order.transactionType === 'SELL' && order.product !== 'MTF') return null;
                                    return (
                                      <Stack spacing={0.25}>
                                        <FormControlLabel
                                          sx={{ m: 0 }}
                                          control={
                                            <Switch
                                              size="small"
                                              checked={mtfToggle[order.requestId] ?? false}
                                              onChange={(e) => setMtfToggle((prev) => ({ ...prev, [order.requestId]: e.target.checked }))}
                                              color="primary"
                                              disabled={order.transactionType !== 'SELL' && !order.accountUseMTF}
                                            />
                                          }
                                          label={
                                            <Typography variant="caption">
                                              {order.transactionType === 'SELL' ? 'Sell from MTF position' : 'Proceed with MTF'}
                                            </Typography>
                                          }
                                        />
                                        {order.transactionType !== 'SELL' && !order.accountUseMTF && (
                                          <Typography
                                            variant="caption"
                                            sx={{
                                              pl: 0.75,
                                              pr: 0.75,
                                              py: 0.25,
                                              borderRadius: 1,
                                              display: 'inline-block',
                                              bgcolor: 'error.lighter',
                                              color: 'error.dark',
                                              fontWeight: 600
                                            }}
                                          >
                                            ⚠ MTF is not enabled in this account. Enable MTF in the user profile to proceed with MTF.
                                          </Typography>
                                        )}
                                        {order.transactionType !== 'SELL' &&
                                          order.accountUseMTF &&
                                          order.product === 'MTF' &&
                                          !mtfToggle[order.requestId] && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                pl: 0.75,
                                                pr: 0.75,
                                                py: 0.25,
                                                borderRadius: 1,
                                                display: 'inline-block',
                                                bgcolor: 'warning.lighter',
                                                color: 'warning.dark',
                                                fontWeight: 600
                                              }}
                                            >
                                              ⚠ This order is suggested as MTF. Enable &quot;Proceed with MTF&quot; to execute it as
                                              intended.
                                            </Typography>
                                          )}
                                        {order.transactionType === 'BUY' &&
                                          (order?.requiredMargin > 0 || (order?.price > 0 && order?.quantity > 0)) && (
                                            <Typography
                                              variant="caption"
                                              sx={{
                                                pl: 0.75,
                                                pr: 0.75,
                                                py: 0.25,
                                                borderRadius: 1,
                                                display: 'inline-block',
                                                ...(mtfToggle[order.requestId]
                                                  ? { bgcolor: 'warning.lighter', color: 'warning.dark', fontWeight: 600 }
                                                  : { color: 'text.secondary' })
                                              }}
                                            >
                                              {mtfToggle[order.requestId]
                                                ? `Margin Required: ${
                                                    order?.requiredMargin > 0
                                                      ? formatNumberToCurrency(order?.requiredMargin, true, CurrencyType.INR)
                                                      : '...'
                                                  }`
                                                : order?.price > 0
                                                ? `Amount Required: ${formatNumberToCurrency(
                                                    order.price * order.quantity,
                                                    true,
                                                    CurrencyType.INR
                                                  )}`
                                                : `Margin Required: ${formatNumberToCurrency(
                                                    order?.requiredMargin,
                                                    true,
                                                    CurrencyType.INR
                                                  )}`}
                                            </Typography>
                                          )}
                                      </Stack>
                                    );
                                  })()}

                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center' }}>
                                  Broker:&nbsp;
                                  {order.brokers}
                                  &nbsp;| &nbsp;Strategy: {order?.scenarioDisplayName ?? '_'}
                                </Typography>
                                {order.basketOrder && (
                                  <Box>
                                    {order?.orderRequestList.map((r, i) => (
                                      <Typography
                                        key={i}
                                        variant="caption"
                                        color="text.secondary"
                                        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
                                      >
                                        <Typography
                                          variant="caption"
                                          color={r.transactionType === 'BUY' ? 'primary' : 'error'}
                                          sx={{ fontWeight: 'bold', mr: 1 }}
                                        >
                                          {r.transactionType}
                                        </Typography>

                                        {r.option === false && (
                                          <Typography variant="caption">
                                            {r.quantity} x{' '}
                                            <Box
                                              component="span"
                                              onClick={() => openStockDetail(r.stockId)}
                                              sx={{
                                                cursor: 'pointer',
                                                textDecoration: 'underline',
                                                textDecorationStyle: 'dotted',
                                                '&:hover': { color: 'primary.main' }
                                              }}
                                            >
                                              {r.stockId}
                                            </Box>{' '}
                                            @ {r.price} (LTP:{' '}
                                            {r.stockId && liveTicks.current[r.stockId]?.price != null
                                              ? liveTicks.current[r.stockId].price
                                              : '--'}
                                            ){getOrderTypeLabel(r.orderType) ? ` — ${getOrderTypeLabel(r.orderType)}` : ''}
                                          </Typography>
                                        )}

                                        {r.option === true && (
                                          <Typography variant="caption">
                                            {r.quantity} x {r.stockId} {r.strikePrice} {r.optionType} @ {r.price}
                                            {getOrderTypeLabel(r.orderType) ? ` — ${getOrderTypeLabel(r.orderType)}` : ''}
                                          </Typography>
                                        )}

                                        {/* Each leg is placed as its own order, so it fills — or fails — on its own. */}
                                        {order.orderRequestStatus === OrderRequestStatus.APPROVED && r.orderStatus && (
                                          <Chip
                                            component="span"
                                            label={
                                              (r.executedQuantity ?? 0) > 0
                                                ? `${getOrderStatusLabel(r.orderStatus)} ${r.executedQuantity} @ ₹${r.executedPrice}`
                                                : getOrderStatusLabel(r.orderStatus)
                                            }
                                            color={getOrderStatusColor(r.orderStatus)}
                                            size="small"
                                            variant="light"
                                          />
                                        )}
                                      </Typography>
                                    ))}
                                  </Box>
                                )}
                              </Stack>
                              <Stack
                                direction="row"
                                spacing={1}
                                alignItems="center"
                                sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}
                              >
                                <Typography
                                  variant="caption"
                                  color={order.transactionType === 'BUY' ? 'primary' : 'error'}
                                  sx={{ ml: 4, mb: 1, mt: -4, fontWeight: 'bold' }}
                                >
                                  {order.transactionType}
                                </Typography>
                                <Grid sx={{ mt: 5 }}>
                                  {order.orderRequestStatus === OrderRequestStatus.OPEN ? (
                                    hasApproveOrderPermission && (
                                      <>
                                        <IconButton
                                          color="error"
                                          aria-label="cancel"
                                          size="medium"
                                          sx={{ p: 0.5 }}
                                          onClick={() =>
                                            handleDeclineOrder(order?.requestId.toString(), order.orderId.toString(), order.userId)
                                          }
                                        >
                                          <CancelIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          color="info"
                                          aria-label="edit"
                                          size="medium"
                                          sx={{ p: 0.5 }}
                                          onClick={() => {
                                            let quantity = order.quantity;

                                            const isBasketOrder = order.basketOrder === true;

                                            if (
                                              isBasketOrder &&
                                              Array.isArray(order.orderRequestList) &&
                                              order.orderRequestList.length > 0
                                            ) {
                                              quantity = order.orderRequestList[0].quantity;
                                            }

                                            setChangeQtyState({
                                              open: true,
                                              requestId: order.requestId?.toString() ?? '',
                                              userId: order.userId ?? '',
                                              currentQty: quantity ?? 0,
                                              currentOrderType: order.orderType ?? null,
                                              currentPrice: order.price ?? 0,
                                              currentTriggerPrice: order.triggerPrice ?? 0,
                                              isBasket: isBasketOrder
                                            });
                                          }}
                                        >
                                          <EditIcon fontSize="small" />
                                        </IconButton>
                                        <IconButton
                                          color="success"
                                          aria-label="approve"
                                          size="medium"
                                          sx={{ p: 0.5 }}
                                          onClick={() =>
                                            handleApproveOrder(
                                              order.requestId.toString(),
                                              mtfToggle[order.requestId] ?? false,
                                              liveQuantity(order) ?? undefined
                                            )
                                          }
                                        >
                                          <CheckCircleIcon fontSize="small" />
                                        </IconButton>
                                      </>
                                    )
                                  ) : (
                                    <Tooltip title={order.failureReason ?? ''}>
                                      <Chip
                                        label={order.orderRequestStatus}
                                        color={
                                          order.orderRequestStatus === OrderRequestStatus.CANCELLED ||
                                          order.orderRequestStatus === OrderRequestStatus.REJECTED ||
                                          order.orderRequestStatus === OrderRequestStatus.FAILED
                                            ? 'error'
                                            : 'success'
                                        }
                                        size="small"
                                        variant="light"
                                      />
                                    </Tooltip>
                                  )}
                                </Grid>
                              </Stack>
                            </Box>
                          </ListItem>
                        ))}
                    </List>
                  )}
                </Grid>
              </Box>
            </SimpleBar>
          </MainCard>
        )}
      </Drawer>
    </>
  );
};

export default OrderRequestDrawer;
